import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Center,
  Divider,
  Heading,
  HStack,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  RiCheckLine,
  RiErrorWarningLine,
  RiTriangleLine,
} from "@remixicon/react";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { Restricted } from "@/app/components/Restricted";
import GithubLogo from "@/common/assets/github-logo.svg?react";
import { EditIconButton } from "@/common/components/CommonIconButtons";
import { DelayedSpinner } from "@/common/components/DelayedSpinner";
import { DocumentationRefButton } from "@/common/components/DocumentationReference";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { compactFormattedDate } from "@/common/utils/datetime";
import { GithubRepositoryAutocompleteSelect } from "@/integration/components/github/GithubRepositoryAutocompleteSelect";
import { useGithubConnection } from "@/integration/data/useGithubConnection";
import { useGithubDeleteMutation } from "@/integration/data/useGithubDeleteMutation";
import { useGithubSaveRepoMutation } from "@/integration/data/useGithubSaveRepoMutation";
import useGithubConnectionUrl from "@/integration/hooks/useGithubConnectionUrl";
import {
  GitHubConnection,
  GithubIntegrationSchema,
} from "@reflag/shared/githubConnectionAPI";

const description = "Automatic flag cleanup within your GitHub repositories.";

function Title() {
  return (
    <HStack>
      <GithubLogo height="16" width="16" />
      <Heading fontWeight="medium" size="sm">
        GitHub
      </Heading>
    </HStack>
  );
}

function Disconnect() {
  const { mutate: deleteMutate, isPending: mutationIsLoading } =
    useGithubDeleteMutation();

  return (
    <Restricted role="admin">
      <Button
        isLoading={mutationIsLoading}
        size="sm"
        variant="outline"
        onClick={() => deleteMutate()}
      >
        Disconnect
      </Button>
    </Restricted>
  );
}

function Connected({
  githubConnection: { repository, createdAt, codeReferencesSyncError },
}: {
  githubConnection: GitHubConnection;
}) {
  const [isEditing, setIsEditing] = useState(!repository);
  const saveRepoMutation = useGithubSaveRepoMutation();

  const form = useForm({
    resolver: zodResolver(GithubIntegrationSchema),
    mode: "onChange",
    defaultValues: {
      repository: repository ?? "",
    },
  });

  const handleSubmit = useFormMutationSubmitHandler(
    form,
    saveRepoMutation,
    (connection) => {
      setIsEditing(false);
      form.reset({
        repository: connection.repository ?? "",
      });
    },
    {
      successToast: "GitHub repository saved",
      errorToast: "Failed to save GitHub repository",
    },
  );

  const rightActions = (
    <ButtonGroup>
      <DocumentationRefButton section="integrations/github" />
      <Disconnect />
    </ButtonGroup>
  );

  return (
    <VStack align="flex-start" spacing={4} w="full">
      <VStack align="flex-start" spacing={2} w="full">
        <HStack justify="space-between" w="full">
          <Title />
          <Badge colorScheme={repository ? "green" : "orange"}>
            <HStack spacing={1}>
              {repository ? (
                <RiCheckLine size={16} />
              ) : (
                <RiTriangleLine size={16} />
              )}
              <Text>
                {repository
                  ? `Since ${compactFormattedDate(createdAt)}`
                  : "Select a repository"}
              </Text>
            </HStack>
          </Badge>
        </HStack>
        <Text color="dimmed" fontSize="sm" w="lg">
          {description}
        </Text>
      </VStack>
      <Divider />
      {codeReferencesSyncError && repository ? (
        <Alert alignItems="flex-start" borderRadius="md" status="warning">
          <AlertIcon />
          <AlertDescription>
            Code references sync issue for {repository}:{" "}
            {codeReferencesSyncError.message}
          </AlertDescription>
        </Alert>
      ) : null}
      {isEditing ? (
        <FormProvider {...form}>
          <VStack
            align="flex-start"
            as="form"
            spacing={4}
            w="full"
            onSubmit={handleSubmit}
          >
            <VStack spacing={2} w="sm">
              <ManagedFormControl
                label="Repository"
                name="repository"
                render={({ field }) => (
                  <GithubRepositoryAutocompleteSelect
                    bg="appBackground"
                    value={field.value}
                    valueAsId
                    onChange={field.onChange}
                  />
                )}
              />
            </VStack>

            <FormRootError />

            <HStack justify="space-between" w="full">
              <ButtonGroup>
                <FormSubmit isLoading={saveRepoMutation.isPending}>
                  Save
                </FormSubmit>
                {repository && (
                  <Button
                    color="dimmed"
                    variant="ghost"
                    onClick={() => setIsEditing(false)}
                  >
                    Cancel
                  </Button>
                )}
              </ButtonGroup>
              {rightActions}
            </HStack>
          </VStack>
        </FormProvider>
      ) : (
        <HStack justify="space-between" w="full">
          <HStack fontSize="sm" spacing={1}>
            <Text color="dimmed">Repository:</Text>
            {repository ? (
              <Link href={`https://github.com/${repository}`}>
                {repository}
              </Link>
            ) : (
              <Text color="dimmed">Not selected</Text>
            )}
            <Restricted role="admin">
              <EditIconButton
                label="Edit repository"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </EditIconButton>
            </Restricted>
          </HStack>
          {rightActions}
        </HStack>
      )}
    </VStack>
  );
}

function NotConnected({ isGuard = false }: { isGuard?: boolean }) {
  const connectionUrl = useGithubConnectionUrl();
  return (
    <VStack align="flex-start" spacing={2} w="full">
      <HStack justify="space-between" w="full">
        <Title />
        {isGuard && (
          <Badge colorScheme="orange">
            <HStack spacing={1}>
              <RiErrorWarningLine size={16} />
              <Text>Not connected</Text>
            </HStack>
          </Badge>
        )}
      </HStack>
      <HStack justify="space-between" w="full">
        <Text color="dimmed" fontSize="sm" maxWidth="lg">
          {description}
        </Text>
        <ButtonGroup alignSelf="flex-end">
          <DocumentationRefButton section="integrations/github" />
          <Restricted flagKey="github-integration" role="admin">
            <Button as="a" href={connectionUrl} size="sm" variant="outline">
              Connect
            </Button>
          </Restricted>
        </ButtonGroup>
      </HStack>
    </VStack>
  );
}

export default function GithubIntegrationForm({
  isGuard = false,
}: {
  isGuard?: boolean;
}) {
  const { data, isLoading } = useGithubConnection();

  return (
    <Card w="full">
      <CardBody w="full">
        {isLoading ? (
          <Center>
            <DelayedSpinner />
          </Center>
        ) : data ? (
          <Connected githubConnection={data} />
        ) : (
          <NotConnected isGuard={isGuard} />
        )}
      </CardBody>
    </Card>
  );
}
